import { FC, ChangeEvent, useState, useEffect, useMemo } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Box, Typography, Link as MuiLink } from '@mui/material'
import { useAuth } from '@frontegg/react'
import { hasRole, Role } from 'lib/utils'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

/* Utils */
import { LabelOption } from '../../../components/select-dropdown/SelectDropdown'
import { FrameworkTemplate, FrameworkType } from 'models'
import axios from 'lib/axios'

/* Components */
import { Container, Content } from './styles/chooseCustomFramework.styles'
import { Header } from '../framework.styles'
import { Breadcrumbs } from '../frameworkConfiguration.styles'
import { ThemeButton } from '../../../components/buttons'
import SelectDropdown from '../../../components/select-dropdown'
import { useThemeColors } from 'hooks/useThemeColors'
import OnyxiaBackgroundCropped from '../../../components/svg/OnyxiaBackgroundCropped'
import OnyxiaBasicModal from '../../../components/modal/OnyxiaBasicModal'
import TextField from '../../../components/form-components/TextField'

const defaultFrameworks = [
  {
    id: FrameworkType.Onyxia,
    title: 'Onyxia Template'
  },
  {
    id: FrameworkType.Nist,
    title: 'NIST Template'
  }
]

const templateNameMap: Record<string, string> = {
  [FrameworkType.Onyxia]: 'Onyxia-based Framework',
  [FrameworkType.Nist]: 'NIST-based Framework',
  hipaa: 'HIPAA-based Framework',
  soc2: 'SOC2-based Framework',
  pci: 'PCI-based Framework'
}

const CustomFrameworkIcon = ({ color }: { color: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="113" height="113" viewBox="0 0 113 113" fill="none">
    <path
      d="M91 61.0489C91.1857 59.5635 91.325 58.0781 91.325 56.4998C91.325 54.9216 91.1857 53.4362 91 51.9508L100.794 44.2916C101.676 43.5953 101.909 42.342 101.352 41.3207L92.0677 25.2597C91.6499 24.517 90.8608 24.0992 90.0252 24.0992C89.7467 24.0992 89.4682 24.1456 89.2361 24.2384L77.6777 28.8804C75.2639 27.0236 72.6644 25.4918 69.8328 24.3313L68.0689 12.0302C67.9296 10.9161 66.9548 10.0806 65.7944 10.0806H47.2267C46.0662 10.0806 45.0914 10.9161 44.9521 12.0302L43.1882 24.3313C40.3566 25.4918 37.7571 27.07 35.3433 28.8804L23.7849 24.2384C23.5064 24.1456 23.2279 24.0992 22.9494 24.0992C22.1602 24.0992 21.3711 24.517 20.9533 25.2597L11.6695 41.3207C11.066 42.342 11.3446 43.5953 12.2265 44.2916L22.021 51.9508C21.8353 53.4362 21.6961 54.968 21.6961 56.4998C21.6961 58.0317 21.8353 59.5635 22.021 61.0489L12.2265 68.7081C11.3446 69.4044 11.1125 70.6577 11.6695 71.6789L20.9533 87.74C21.3711 88.4827 22.1602 88.9005 22.9958 88.9005C23.2743 88.9005 23.5528 88.8541 23.7849 88.7612L35.3433 84.1193C37.7571 85.9761 40.3566 87.5079 43.1882 88.6684L44.9521 100.969C45.0914 102.084 46.0662 102.919 47.2267 102.919H65.7944C66.9548 102.919 67.9296 102.084 68.0689 100.969L69.8328 88.6684C72.6644 87.5079 75.2639 85.9297 77.6777 84.1193L89.2361 88.7612C89.5146 88.8541 89.7931 88.9005 90.0716 88.9005C90.8608 88.9005 91.6499 88.4827 92.0677 87.74L101.352 71.6789C101.909 70.6577 101.676 69.4044 100.794 68.7081L91 61.0489ZM81.809 53.1112C81.9947 54.5502 82.0411 55.525 82.0411 56.4998C82.0411 57.4746 81.9483 58.4959 81.809 59.8884L81.1591 65.1338L85.2905 68.3832L90.3037 72.2824L87.0544 77.8991L81.1591 75.5317L76.3315 73.5821L72.1538 76.7386C70.1578 78.2241 68.2546 79.3381 66.3514 80.1273L61.431 82.1233L60.6882 87.3687L59.7599 93.6353H53.2612L51.6365 82.1233L46.716 80.1273C44.72 79.2917 42.8632 78.2241 41.0065 76.8315L36.7823 73.5821L31.8619 75.5782L25.9666 77.9455L22.7173 72.3288L27.7306 68.4296L31.8619 65.1802L31.212 59.9349C31.0727 58.4959 30.9799 57.4282 30.9799 56.4998C30.9799 55.5715 31.0727 54.5038 31.212 53.1112L31.8619 47.8659L27.7306 44.6165L22.7173 40.7173L25.9666 35.1006L31.8619 37.4679L36.6895 39.4175L40.8672 36.261C42.8632 34.7756 44.7664 33.6616 46.6696 32.8724L51.5901 30.8764L52.3328 25.631L53.2612 19.3644H59.7134L61.3381 30.8764L66.2586 32.8724C68.2546 33.708 70.1114 34.7756 71.9681 36.1682L76.1923 39.4175L81.1127 37.4215L87.008 35.0541L90.2573 40.6709L85.2905 44.6165L81.1591 47.8659L81.809 53.1112ZM56.5105 37.9321C46.2518 37.9321 37.9428 46.2412 37.9428 56.4998C37.9428 66.7585 46.2518 75.0676 56.5105 75.0676C66.7692 75.0676 75.0782 66.7585 75.0782 56.4998C75.0782 46.2412 66.7692 37.9321 56.5105 37.9321ZM56.5105 65.7837C51.4044 65.7837 47.2267 61.606 47.2267 56.4998C47.2267 51.3937 51.4044 47.216 56.5105 47.216C61.6166 47.216 65.7944 51.3937 65.7944 56.4998C65.7944 61.606 61.6166 65.7837 56.5105 65.7837Z"
      fill={color}
    />
  </svg>
)

const BuildFromScratchIcon = ({ color }: { color: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46" fill="none">
    <path
      d="M40.9073 35.2649L31.0054 25.363H29.1558L24.4104 30.1084V31.958L34.3122 41.8599C35.0409 42.5885 36.2179 42.5885 36.9465 41.8599L40.9073 37.8992C41.6359 37.1892 41.6359 35.9935 40.9073 35.2649ZM35.62 37.9178L27.6985 29.9963L29.025 28.6699L36.9465 36.5914L35.62 37.9178Z"
      fill={color}
    />
    <path
      d="M32.8176 20.356L35.4519 17.7217L39.4126 21.6825C41.5985 19.4966 41.5985 15.9469 39.4126 13.761L32.7989 7.14728L30.1647 9.78155V4.513L28.8569 3.18652L22.2432 9.80023L23.5696 11.1267H28.8569L26.2226 13.761L28.203 15.7414L22.8036 21.1407L15.0876 13.4247V10.7717L9.44544 5.12953L4.1582 10.4168L9.81909 16.0777H12.4534L20.1694 23.7936L18.5813 25.3817H14.6206L4.71869 35.2836C3.99006 36.0122 3.99006 37.1892 4.71869 37.9178L8.67944 41.8786C9.40807 42.6072 10.5851 42.6072 11.3137 41.8786L21.2156 31.9767V28.016L30.8372 18.3943L32.8176 20.356ZM17.9087 29.9777L9.98724 37.8992L8.66076 36.5727L16.5823 28.6512L17.9087 29.9777Z"
      fill={color}
    />
  </svg>
)

const TemplateIcon = ({ color }: { color: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46" fill="none">
    <path
      d="M31.0278 1.96484H8.52783C6.46533 1.96484 4.77783 3.65234 4.77783 5.71484V31.9648H8.52783V5.71484H31.0278V1.96484ZM29.1528 9.46484H16.0278C13.9653 9.46484 12.2966 11.1523 12.2966 13.2148L12.2778 39.4648C12.2778 41.5273 13.9466 43.2148 16.0091 43.2148H36.6528C38.7153 43.2148 40.4028 41.5273 40.4028 39.4648V20.7148L29.1528 9.46484ZM16.0278 39.4648V13.2148H27.2778V22.5898H36.6528V39.4648H16.0278Z"
      fill={color}
    />
  </svg>
)

const STATIC_RESERVED_NAMES = ['onyxia', 'nist']

const ChooseCustomFramework: FC = () => {
  const getColor = useThemeColors()
  const navigate = useNavigate()
  const { user: authUser } = useAuth()
  const [templateValue, setTemplateValue] = useState<string[]>([])
  const [templates, setTemplates] = useState<FrameworkTemplate[]>(defaultFrameworks)
  const [loading, setLoading] = useState(true)
  const [isNameModalOpen, setIsNameModalOpen] = useState(false)
  const [frameworkName, setFrameworkName] = useState('')
  const [selectedTemplate, setSelectedTemplate] = useState<FrameworkType | null>(null)
  const [isFromScratch, setIsFromScratch] = useState(false)

  const fetchFrameworkTemplates = async () => {
    try {
      const res = await axios.get<{ data: { templates: FrameworkTemplate[] } }>('/api/v3/frameworks/custom/templates')

      if (res.status.toString().includes('20')) {
        setTemplates(res.data.data.templates)
      }
    } catch (err) {
      console.error(err)
    } finally {
      setTimeout(() => {
        setLoading(false)
      }, 1000)
    }
  }

  useEffect(() => {
    fetchFrameworkTemplates().catch((e) => e)
  }, [])

  const handleSelectFramework = (fw: FrameworkType): void => {
    const template = templates.find((t) => t.id === fw)
    const baseName = template ? template.title.replace(/\s*Template$/i, '') : fw
    const defaultName = templateNameMap[fw] || `${baseName}-based Framework`
    setFrameworkName(defaultName)
    setSelectedTemplate(fw)
    setIsFromScratch(false)
    setIsNameModalOpen(true)
  }

  const handleOpenCreateFromScratch = () => {
    setFrameworkName('')
    setSelectedTemplate(null)
    setIsFromScratch(true)
    setIsNameModalOpen(true)
  }

  const handleCloseNameModal = () => {
    setIsNameModalOpen(false)
    setFrameworkName('')
    setSelectedTemplate(null)
    setIsFromScratch(false)
  }

  const reservedFrameworkNames = useMemo(() => {
    return new Set(STATIC_RESERVED_NAMES)
  }, [])

  const isReservedName = useMemo(() => {
    const trimmed = frameworkName.trim().toLowerCase()
    if (!trimmed) return false
    return reservedFrameworkNames.has(trimmed)
  }, [frameworkName, reservedFrameworkNames])

  const handleConfirmFrameworkName = () => {
    if (!frameworkName.trim() || isReservedName) return

    if (isFromScratch) {
      navigate('/framework-configuration/custom?template=blank', {
        state: { blank: true, frameworkName: frameworkName.trim() }
      })
    } else if (selectedTemplate) {
      navigate(`/framework-configuration/custom?template=${selectedTemplate}`, {
        state: { template: selectedTemplate, frameworkName: frameworkName.trim() }
      })
    }
    handleCloseNameModal()
  }

  const templateDropdownOptions: LabelOption[] = useMemo(
    () =>
      templates.map((template) => ({
        name: template.id,
        label: template.title,
        action: async () => handleSelectFramework(template.id)
      })),
    [templates, loading]
  )

  const breadcrumbs = [
    <MuiLink component={Link} underline="none" key="1" color="inherit" to="/framework-configuration">
      Frameworks
    </MuiLink>,
    <MuiLink component={Link} underline="none" key="2" color="inherit" to={`/framework-configuration/custom`}>
      Custom
    </MuiLink>
  ]

  const handleCreateCategory = () => {
    handleOpenCreateFromScratch()
  }

  const isDemoUser = useMemo(() => hasRole(Role.Demo, authUser), [authUser])

  return (
    <Container className="choose-custom-framework-container">
      <OnyxiaBackgroundCropped />

      <Header className="header">
        <Breadcrumbs separator={<NavigateNextIcon fontSize="medium" />} aria-label="breadcrumb">
          {breadcrumbs}
        </Breadcrumbs>
      </Header>

      <Content className="content-wrapper">
        <Box className="content-main">
          <Box className="content-main-image">
            <CustomFrameworkIcon color={getColor('cta')} />
          </Box>
          <Box className="content-main-text-wrapper">
            <Typography className="content-main-title">Custom Framework</Typography>
            <Typography className="content-main-text">Not Configured Yet</Typography>
          </Box>
        </Box>

        <Box className="content-select-wrapper">
          <Box className="content-select-item">
            <Box className="content-select-item__image-wrapper">
              <BuildFromScratchIcon color={getColor('cta')} />
            </Box>
            <Box className="content-select-item__text-wrapper">
              <Typography className="content-select-item__title">Build From Scratch</Typography>
              <Typography className="content-select-item__text">
                Create your Categories manually and assign CPIs
              </Typography>
            </Box>
            <ThemeButton themevariant="primary" onClick={handleCreateCategory}>
              <>
                <ArrowForwardIcon />
                Create category
              </>
            </ThemeButton>
          </Box>
          <Typography className="separator-text">Or</Typography>
          <Box className="content-select-item">
            <Box className="content-select-item__image-wrapper">
              <TemplateIcon color={getColor('cta')} />
            </Box>
            <Box className="content-select-item__text-wrapper">
              <Typography className="content-select-item__title">Use a Template</Typography>
              <Typography className="content-select-item__text">
                Duplicate a framework to use it as your template
              </Typography>
            </Box>
            <SelectDropdown
              disabled={loading}
              options={templateDropdownOptions}
              value={templateValue}
              label=""
              placeholder="Choose Template"
              handleChange={(e: any) => {
                setTemplateValue([e.target.value])
                handleSelectFramework(e.target.value)
              }}
              hideSearch={true}
              minWidth="190px"
              disableForDemo={isDemoUser}
            />
          </Box>
        </Box>
      </Content>

      <OnyxiaBasicModal
        isOpen={isNameModalOpen}
        title="Create custom framework"
        description="Set the name for your custom framework"
        submitButtonText="Create"
        cancelButtonText="Cancel"
        onSubmit={handleConfirmFrameworkName}
        onCancel={handleCloseNameModal}
        submitDisabled={!frameworkName.trim() || isReservedName}
      >
        <TextField
          name="frameworkName"
          label="Framework Name"
          placeholder="Enter a name..."
          value={frameworkName}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setFrameworkName(e.target.value)}
          inputProps={{ maxLength: 50 }}
          helperText={
            isReservedName
              ? 'This name is already used by an existing framework. Please choose a different name.'
              : undefined
          }
          sx={{
            '.helper-text': {
              fontFamily: 'Quicksand, sans-serif',
              fontSize: '12px',
              lineHeight: '22px',
              letterSpacing: '0.25px'
            }
          }}
        />
      </OnyxiaBasicModal>
    </Container>
  )
}

export default ChooseCustomFramework
